import React, { useMemo, useState } from "react";
import { Button, Group, Menu, Modal, Stack, Text } from "@mantine/core";
import { FaCheckCircle, FaChevronDown, FaTimesCircle } from "react-icons/fa";
import {
  usePostMultiplacerRoomsRoomNumberingApprovalBulkClearMutation,
  usePostMultiplacerRoomsRoomNumberingApprovalBulkMutation,
} from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setSelectedRoomsInRedux, thunkToast } from "../DesignTab/designSlice";

type PendingAction = "approve" | "clearApproval" | null;

export const SelectedRoomsRoomNumberingActionsMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectNo = useAppSelector((state) => state.design.projectNo);
  const selectedRooms = useAppSelector((state) => state.design.selectedRooms);
  const selectedCount = selectedRooms.length;
  const hasSelectedRooms = selectedCount > 0;
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const [approveRooms, approveRoomsState] =
    usePostMultiplacerRoomsRoomNumberingApprovalBulkMutation();
  const [clearApproval, clearApprovalState] =
    usePostMultiplacerRoomsRoomNumberingApprovalBulkClearMutation();

  const isLoading = approveRoomsState.isLoading || clearApprovalState.isLoading;
  const pendingActionLabel =
    pendingAction === "approve" ? "godkjenne romnummerering for" : "fjerne godkjenning for";
  const pendingActionTitle =
    pendingAction === "approve" ? "Godkjenn romnummerering" : "Fjern godkjenning";

  const label = useMemo(() => {
    if (selectedCount === 0) return "Ingen rom valgt";
    if (selectedCount === 1) return "1 rom valgt";
    return `${selectedCount} rom valgt`;
  }, [selectedCount]);

  const showResultToast = (args: {
    header: string;
    updated: number;
    requested: number;
    skippedMissingValue?: number;
  }) => {
    const skippedText =
      args.skippedMissingValue != null && args.skippedMissingValue > 0
        ? ` ${args.skippedMissingValue} rom manglet verdi og ble hoppet over.`
        : "";

    dispatch(
      thunkToast({
        header: args.header,
        text: `${args.updated} av ${args.requested} rom oppdatert.${skippedText}`,
        isSuccess: true,
      })
    );
  };

  const handleApproveSelectedRooms = async () => {
    if (!projectNo || selectedRooms.length === 0) return;

    const result = await approveRooms({
      body: {
        projectNo,
        roomNumbers: selectedRooms,
      },
    }).unwrap();

    showResultToast({
      header: "Romnummerering godkjent",
      updated: result.counts.updated,
      requested: result.counts.requested,
      skippedMissingValue: result.counts.skippedMissingValue,
    });
    dispatch(setSelectedRoomsInRedux([]));
    setPendingAction(null);
  };

  const handleClearApprovalSelectedRooms = async () => {
    if (!projectNo || selectedRooms.length === 0) return;

    const result = await clearApproval({
      body: {
        projectNo,
        roomNumbers: selectedRooms,
      },
    }).unwrap();

    showResultToast({
      header: "Godkjenning fjernet",
      updated: result.counts.updated,
      requested: result.counts.requested,
    });
    dispatch(setSelectedRoomsInRedux([]));
    setPendingAction(null);
  };

  const handleConfirmPendingAction = async () => {
    if (pendingAction === "approve") {
      await handleApproveSelectedRooms();
      return;
    }

    if (pendingAction === "clearApproval") {
      await handleClearApprovalSelectedRooms();
    }
  };

  return (
    <>
      <Group gap="sm" wrap="nowrap">
        <Text size="sm" fw={600}>
          {label}
        </Text>

        <Menu shadow="md" width={280} position="bottom-end">
          <Menu.Target>
            <Button
              size="xs"
              variant="filled"
              rightSection={<FaChevronDown size={12} />}
              disabled={!hasSelectedRooms || !projectNo || isLoading}
              loading={isLoading}
            >
              Handlinger
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Valgte rom</Menu.Label>
            <Menu.Item
              leftSection={<FaCheckCircle size={14} />}
              onClick={() => setPendingAction("approve")}
            >
              Godkjenn romnummerering
            </Menu.Item>
            <Menu.Item
              leftSection={<FaTimesCircle size={14} />}
              color="red"
              onClick={() => setPendingAction("clearApproval")}
            >
              Fjern godkjenning
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Modal
        opened={pendingAction != null}
        onClose={() => {
          if (!isLoading) {
            setPendingAction(null);
          }
        }}
        title={pendingActionTitle}
        centered
      >
        <Stack gap="lg">
          <Text size="sm">
            Vil du {pendingActionLabel} {selectedCount} valgte rom?
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" disabled={isLoading} onClick={() => setPendingAction(null)}>
              Avbryt
            </Button>
            <Button
              color={pendingAction === "clearApproval" ? "red" : "blue"}
              loading={isLoading}
              onClick={() => void handleConfirmPendingAction()}
            >
              Bekreft
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
